import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { db } from "@/firebase";
import { fetchPlayerInfo } from "@/firebase/playerUtils";
import { idJetonValide, statutDuJeton } from "./jeton";
import { registrePlein, scansDuRegistre } from "./registre";

export const SCAN_STATES = {
  UNCLAIMED: "unclaimed",
  CLAIMED_UNSCANNED: "claimed-unscanned",
  OWNED: "owned",
  CLAIMED_SCANNED: "claimed-scanned",
};

const getDefaultState = () => ({
  token: null,
  // "loading" | "found" | "not-found" | "offline" (en attente du serveur)
  // | "invalid" (QR qui n'est pas un id de jeton) : cf. jeton.js.
  tokenStatus: "loading",
  animax: null,
  habitat: null,
  scanState: null,
  firstScan: null,
  myScan: null,
  scanHistory: [],
  scanCount: 0,
  owner: null,
  // A11 : { plein } si claims/{tid} existe, null sinon (jeton jamais scanné
  // ou scanné avant la migration des registres).
  registre: null,
});

const store = {
  state: () => getDefaultState(),
  mutations: {
    RESET_SCAN_STATE(state) {
      Object.assign(state, getDefaultState());
    },
    SET_ANIMAX(state, animax) {
      state.animax = animax;
    },
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_TOKEN_STATUS(state, status) {
      state.tokenStatus = status;
    },
    SET_HABITAT(state, habitat) {
      state.habitat = habitat;
    },
    SET_SCAN_CONTEXT(state, ctx) {
      state.scanState = ctx.scanState;
      state.firstScan = ctx.firstScan;
      state.myScan = ctx.myScan;
      state.scanHistory = ctx.scanHistory;
      state.scanCount = ctx.scanCount;
      state.registre = ctx.registre ?? null;
    },
    SET_OWNER(state, owner) {
      state.owner = owner;
    },
  },
  actions: {
    // Cf. jeton.js : id validé avant Firestore ; « introuvable » seulement
    // sur la parole du serveur. `includeMetadataChanges` : quand le serveur
    // confirme un document absent, seules les métadonnées changent (fromCache
    // passe à false) ; sans cette option, cet instantané n'arriverait jamais.
    // L'écoute reste ouverte hors ligne : le SDK se reconnecte seul quand le
    // réseau revient, et le statut suit.
    fetchToken({ commit }, id) {
      if (!idJetonValide(id)) {
        commit("SET_TOKEN_STATUS", "invalid");
        return () => {};
      }
      const horsLigne = typeof navigator !== "undefined" && navigator.onLine === false;
      commit("SET_TOKEN_STATUS", horsLigne ? "offline" : "loading");
      let dernier = null;
      return onSnapshot(
        doc(db, "tokens", id),
        { includeMetadataChanges: true },
        (snap) => {
          // Un jeton sans animal lié vaut un jeton absent (A9) : sinon le loader
          // attendait un animal qui ne viendrait jamais.
          const existe = snap.exists() && !!snap.data()?.animax;
          const statut = statutDuJeton({ existe, fromCache: snap.metadata.fromCache });
          if (statut === dernier) return;
          dernier = statut;
          if (statut === "found") commit("SET_TOKEN", { id, ...snap.data() });
          commit("SET_TOKEN_STATUS", statut);
        },
        (e) => {
          console.error("[scan] lecture du jeton", e);
          commit("SET_TOKEN_STATUS", "offline");
        }
      );
    },

    async loadScanContext({ commit, rootState }, tokenId) {
      const currentUid = rootState.currentUser.uid;
      const scansRef = collection(db, "scans");

      // A11 : propriétaire et historique viennent du registre claims/{tid}.
      const claimSnap = await getDoc(doc(db, "claims", tokenId));
      const claim = claimSnap.exists() ? claimSnap.data() : null;
      let scans;
      if (claim) {
        scans = scansDuRegistre(claim);
        // Registre plein : je n'ai pas pu m'y inscrire, mais mes scans à moi
        // restent lisibles (et me gardent en CLAIMED_SCANNED).
        if (registrePlein(claim) && !scans.some((s) => s.uid === currentUid)) {
          const miens = await getDocs(
            query(scansRef, where("uid", "==", currentUid), where("tid", "==", tokenId))
          );
          if (!miens.empty) scans.push({ uid: currentUid, date: miens.docs[0].data().date });
        }
      } else {
        // Transition : jeton scanné avant la migration des registres. L'ancien
        // listage par jeton marche tant que les règles A11 ne sont pas
        // déployées ; ensuite il est refusé et le registre existe.
        try {
          const q = query(scansRef, where("tid", "==", tokenId), orderBy("date", "asc"));
          const snapshot = await getDocs(q);
          scans = snapshot.docs.map((d) => ({ docId: d.id, ...d.data() }));
        } catch {
          scans = [];
        }
      }
      const firstScan = scans[0] ?? null;
      const myScan = scans.find((s) => s.uid === currentUid) ?? null;

      const historyRaw = [...scans]
        .reverse()
        .filter((s) => !firstScan || s.uid !== firstScan.uid)
        .slice(0, 3);

      const uidsToFetch = new Set(historyRaw.map((s) => s.uid));
      if (firstScan && firstScan.uid !== currentUid) uidsToFetch.add(firstScan.uid);
      const infoEntries = await Promise.all(
        [...uidsToFetch].map(async (uid) => [uid, await fetchPlayerInfo(uid)])
      );
      const infoMap = Object.fromEntries(infoEntries);

      const scanHistory = historyRaw.map((s) => ({
        uid: s.uid,
        date: s.date,
        username: infoMap[s.uid]?.username || null,
        avatarId: infoMap[s.uid]?.avatarId || null,
      }));

      let scanState;
      if (!firstScan) scanState = SCAN_STATES.UNCLAIMED;
      else if (firstScan.uid === currentUid) scanState = SCAN_STATES.OWNED;
      else if (myScan) scanState = SCAN_STATES.CLAIMED_SCANNED;
      else scanState = SCAN_STATES.CLAIMED_UNSCANNED;

      commit("SET_SCAN_CONTEXT", {
        scanState,
        firstScan,
        myScan,
        scanHistory,
        scanCount: scans.length,
        registre: claim ? { plein: registrePlein(claim) } : null,
      });

      if (firstScan && firstScan.uid !== currentUid) {
        const info = infoMap[firstScan.uid];
        commit("SET_OWNER", {
          uid: firstScan.uid,
          username: info?.username || null,
          avatarId: info?.avatarId || null,
          animalIds: info?.animalIds || [],
        });
      }

      return { scanState, firstScan, myScan, scans };
    },
  },
  getters: {
    hasAnimaxInCollection: (_, __, rootState) => (animaxId) => {
      return !!rootState.player?.animals?.[animaxId];
    },
    hasHabitatInCollection: (_, __, rootState) => (habitatId) => {
      return !!rootState.player?.habitats?.[habitatId];
    },
  },
};

export default store;
